<script setup>
import BaseModal from "@/components/global-themed/BaseModal.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {ref, watch} from "vue";
import {useAnnotationMutations} from "@/composables/admin/useSeoScoreAnnotations";
import moment from "moment";
import DefaultInput from "@/components/global-themed/inputs/DefaultInput.vue";
import DefaultTextarea from "@/components/global-themed/inputs/DefaultTextarea.vue";
import ModalContentWrapper from "@/components/global-themed/ui/modals/ModalContentWrapper.vue";
import ModalHeading from "@/components/global-themed/ui/modals/ModalHeading.vue";
import ModalHeader from "@/components/global-themed/ui/modals/ModalHeader.vue";

const props = defineProps({
  annotation: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);

const {createAnnotationMutation, updateAnnotationMutation} = useAnnotationMutations();
const {mutateAsync: createAnnotation, isPending: createIsPending} = createAnnotationMutation;
const {mutateAsync: updateAnnotation, isPending: updateIsPending} = updateAnnotationMutation;

const formData = ref({
  title: '',
  date: moment().format('YYYY-MM-DD'),
  description: '',
});

const errors = ref({});

watch(() => props.annotation, (annotation) => {
  if (annotation) {
    formData.value = {
      title: annotation.title,
      date: moment(annotation.date).format('YYYY-MM-DD'),
      description: annotation.description,
    };
  } else {
    formData.value = {
      title: '',
      date: moment().format('YYYY-MM-DD'),
      description: '',
    };
  }
  errors.value = {};
}, { immediate: true });

const handleSubmit = async () => {
  try {
    if (props.annotation) {
      await updateAnnotation({
        id: props.annotation.id,
        data: formData.value,
      });
      handleClose();
      return;
    }

    await createAnnotation(formData.value);
    handleClose();
  } catch (error) {
    if (error.response?.data?.errors) {
      errors.value = error.response.data.errors;
    }
  }
};

const handleClose = () => {
  formData.value = {
    title: '',
    date: moment().format('YYYY-MM-DD'),
    description: '',
  };
  errors.value = {};
  emit('close');
};
</script>

<template>
  <BaseModal
    modal-class="tw-w-[800px]"
    @close="handleClose"
  >
    <ModalContentWrapper>
      <ModalHeader>
        <ModalHeading> {{ annotation ? 'Edit Annotation' : 'Create New Annotation' }}</ModalHeading>
      </ModalHeader>
      <div class="tw-flex tw-flex-col tw-gap-6">
        <DefaultInput
          id="title"
          v-model="formData.title"
          label="Title"
          :validation-errors="errors.title"
        />

        <DefaultInput
          id="date"
          v-model="formData.date"
          label="Date"
          type="date"
          :validation-errors="errors.date"
        />

        <DefaultTextarea
          id="description"
          v-model="formData.description"
          type="text"
          label="Description"
          :validation-errors="errors.description"
        />

        <div class="tw-flex tw-gap-3 tw-justify-end">
          <DefaultButton
            variant="danger"
            @click="handleClose"
          >
            Cancel
          </DefaultButton>
          <DefaultButton
            variant="primary"
            :disabled="createIsPending || updateIsPending"
            :loading="createIsPending || updateIsPending"
            @click="handleSubmit"
          >
            {{ annotation ? 'Update' : 'Create' }}
          </DefaultButton>
        </div>
      </div>
    </ModalContentWrapper>
  </BaseModal>
</template>
